import { Flag, MoreVertical, Trash2 } from "lucide-react";
import { resolveProductImage } from "@/assets/products";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { TruncatedText } from "@/components/ui/truncated-text";
import { cn } from "@/lib/utils";
import { ReactNode } from "react";
import { Link } from "react-router-dom";

interface ProductCardLargeProps {
  name: string;
  /** AUT-399: when set, the name becomes a real link so the product opens in a new tab. */
  nameHref?: string;
  imageUrl?: string;
  asin?: string;
  onClick?: () => void;
  onDelete?: () => void;
  /**
   * Rendered over the image's upper-left corner. Independent of the kebab's gate and
   * deliberately **not** hover-gated — unlike the kebab beside it
   * (`design-input/product-lifecycle-states/prototype-extracted.md § The card overlay`).
   */
  topLeftBadge?: ReactNode;
  /** When set, the kebab offers `Set lifecycle state` above the destructive item. */
  onSetLifecycleState?: () => void;
  className?: string;
  children?: ReactNode;
}

export function ProductCardLarge({
  name,
  nameHref,
  imageUrl,
  asin,
  onClick,
  onDelete,
  topLeftBadge,
  onSetLifecycleState,
  className,
  children,
}: ProductCardLargeProps) {
  const resolvedImage = resolveProductImage(imageUrl || "/placeholder.svg", asin);

  const productImage = (
    <img
      src={resolvedImage}
      alt={name}
      className={cn(
        "w-full h-full object-contain transition-transform duration-300",
        onClick && "group-hover:scale-105",
      )}
    />
  );

  return (
    <Card
      variant={onClick ? "interactive" : "default"}
      onClick={onClick}
      className={cn("overflow-hidden group relative flex h-full flex-col", className)}
    >
      {topLeftBadge && <div className="absolute top-2 left-2 z-10">{topLeftBadge}</div>}
      {(onDelete || onSetLifecycleState) && (
        <div
          className="absolute top-2 right-2 z-10 opacity-0 group-hover:opacity-100 transition-opacity"
          onClick={(e) => e.stopPropagation()}
        >
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 bg-card/90 backdrop-blur-sm border border-border hover:bg-card"
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-popover z-50">
              {onSetLifecycleState && (
                <DropdownMenuItem
                  onSelect={(e) => {
                    e.preventDefault();
                    onSetLifecycleState();
                  }}
                >
                  <Flag className="h-4 w-4 mr-2" />
                  Set lifecycle state
                </DropdownMenuItem>
              )}
              {onDelete && (
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onSelect={(e) => {
                    e.preventDefault();
                    onDelete();
                  }}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Remove from Signal
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
      {/*
        AUT-279 (reopened): the box is bg-white, not a surface tint. object-contain
        letterboxes the roughly 2:1 Amazon source image inside this 1:1 box, and the
        client rejected the grey bars that left. Amazon source images are white-backed,
        so a white box makes the bars disappear into the photo. Do not restore a tint.
      */}
      {/*
        AUT-399 follow-up: the image is a second link to the same product, so the card's largest
        target can be cmd/middle/right-clicked into a new tab -- the thing QA asked for. A sibling
        link rather than a wrapper around the whole card: `<a>` may not contain interactive content
        (nor may it contain a `<button>`), so wrapping would invalidate the ASIN link and the kebab,
        and a stretched overlay would cover them unless each were raised by hand -- silently
        breaking anything added later. Marked aria-hidden + tabIndex -1 so assistive tech and the
        keyboard see exactly one link per card, the title's.
      */}
      <div className="aspect-square w-full bg-white overflow-hidden">
        {nameHref ? (
          <Link
            to={nameHref}
            aria-hidden="true"
            tabIndex={-1}
            onClick={(e) => e.stopPropagation()}
            className="block h-full w-full"
          >
            {productImage}
          </Link>
        ) : (
          productImage
        )}
      </div>
      <div className="flex flex-1 flex-col gap-1.5 p-3">
        {/*
          AUT-249: the name box is a fixed two-line height (h-10) so a
          one-line name reserves the same vertical space as a two-line one,
          and the ASIN + metrics block below is pinned to the bottom
          (mt-auto) so it lines up across every card in a grid row
          regardless of name length. Do not simplify these away.

          AUT-237: the `line-clamp-2` that used to sit on this `<p>` moved into
          `TruncatedText`, which needs to own the clipping in order to measure it
          and reveal the full name on hover. The `h-10` box above is unaffected --
          it reserves the height, `TruncatedText` does the clamping. Putting
          `line-clamp-2` back on the `<p>` would clamp the text a second time,
          outside the element that measures it.
        */}
        <p className="h-10 text-sm font-semibold text-foreground leading-snug">
          {nameHref ? (
            // stopPropagation so the card's own click handler doesn't fire alongside the link.
            <Link to={nameHref} onClick={(e) => e.stopPropagation()} className="hover:underline">
              <TruncatedText lines={2}>{name}</TruncatedText>
            </Link>
          ) : (
            <TruncatedText lines={2}>{name}</TruncatedText>
          )}
        </p>
        <div className="mt-auto space-y-1.5">
          {asin && (
            <a
              href={`https://www.amazon.com/dp/${asin}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="text-xs text-primary hover:underline inline-block"
            >
              {asin}
            </a>
          )}
          {children}
        </div>
      </div>
    </Card>
  );
}
